/* eslint-disable */
import React, { useState } from "react";
import { Flame, Octagon, Play, AlertTriangle, Sliders, BarChart2, Download, FileText, Activity, ShieldAlert, Zap, X } from "lucide-react";
import axios from "axios";

import { API_BASE } from "../config/apiConfig";

export default function StressTesting() {
  const [activeTab, setActiveTab] = useState("builder");
  const [targetUrl, setTargetUrl] = useState("https://jsonplaceholder.typicode.com/posts");
  const [projectName, setProjectName] = useState("CRMFarm E-Commerce");
  
  // Dynamic Stress Profile Configuration
  const [startVus, setStartVus] = useState(50);
  const [stepIncrementVus, setStepIncrementVus] = useState(500);
  const [maxVus, setMaxVus] = useState(3500);
  const [stepDuration, setStepDuration] = useState(15);
  const [errorThreshold, setErrorThreshold] = useState(10);
  const [targetAuthConfirmed, setTargetAuthConfirmed] = useState(true);

  const [steps, setSteps] = useState([
    { name: "Step 1: Authenticate Merchant", method: "POST", endpoint: "/api/v1/auth/login" },
    { name: "Step 2: Query High Volume Catalog", method: "GET", endpoint: "/api/v1/products/search" },
    { name: "Step 3: Process High Concurrency Checkout", method: "POST", endpoint: "/api/v1/checkout/process" }
  ]);

  const [loading, setLoading] = useState(false);
  const [runId, setRunId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const handleRunStressTest = async () => {
    if (!targetAuthConfirmed) {
      alert("Please confirm target authorization before initiating high concurrency stress testing.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setTestResult(null);

    try {
      const payload = {
        name: `Stress Test - ${projectName}`,
        project: projectName,
        target_url: targetUrl,
        vu_count: Number(maxVus),
        ramp_up_duration: Number(stepDuration),
        steady_state_duration: Number(stepDuration * 2),
        ramp_down_duration: Number(stepDuration),
        think_time: 1,
        test_type: "stress",
        thresholds: {
          error_rate: Number(errorThreshold),
          p95_latency: 2000
        },
        steps: steps.map(s => ({
          name: s.name,
          method: s.method,
          path: s.endpoint
        }))
      };

      const res = await axios.post(`${API_BASE}/api/load-testing/definitions`, payload);
      const defId = res.data.id || res.data.definition_id;

      // Launch execution run
      const runRes = await axios.post(`${API_BASE}/api/load-testing/definitions/${defId}/run`);
      const newRunId = runRes.data.run_id;
      setRunId(newRunId);

      // Poll status for genuine execution metrics
      pollStatus(newRunId);
    } catch (err) {
      setLoading(false);
      const detail = err.response?.data?.detail || err.message || "Failed to execute stress test against configured target URL.";
      setErrorMsg(`Execution Connection Error: ${detail}`);
    }
  };

  const handleStopTest = async () => {
    if (!runId) return;
    try {
      await axios.post(`${API_BASE}/api/load-testing/runs/${runId}/stop`);
    } catch (err) {
      console.error("Stop signal error:", err);
    }
  };

  const pollStatus = (rId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/load-testing/runs/${rId}`);
        const status = res.data.status;
        
        if (res.data.current_metrics || res.data.results) {
          const resultsObj = res.data.results || res.data.stress_analysis;
          setTestResult(resultsObj);
        }

        if (status === "completed" || status === "stopped" || status === "failed") {
          clearInterval(interval);
          setLoading(false);
          setActiveTab("report");
          
          if (res.data.error_message) {
            setErrorMsg(res.data.error_message);
          }
        }
      } catch (err) {
        clearInterval(interval);
        setLoading(false);
        setErrorMsg("Failed to poll execution telemetry from backend.");
      }
    }, 2000);
  };

  const handleExportJSON = () => {
    const dataToExport = {
      execution_id: runId || `STRESS-RUN-${Date.now()}`,
      project_name: projectName,
      target_url: targetUrl,
      start_vus: startVus,
      max_vus: maxVus,
      step_duration_s: stepDuration,
      error_threshold_pct: errorThreshold,
      generated_at: new Date().toISOString(),
      metrics: testResult || {}
    };
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Stress_Test_Report_${projectName.replace(/\s+/g, '_')}_${runId || 'latest'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const curve = testResult?.degradation_curve || [];
    let csvContent = "Load Stage (VUs),p50 Latency,p90 Latency,p95 Latency,p99 Latency,Error Rate %,Throughput (RPS),Status\n";
    curve.forEach(row => {
      csvContent += `${row.vus},"${row.p50_latency || ''}","${row.p90_latency || ''}","${row.p95_latency || ''}","${row.p99_latency || ''}","${row.error_rate || ''}",${row.rps || 0},"${row.status || ''}"\n`;
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Stress_Test_Degradation_Curve_${projectName.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportHTML = () => {
    const curve = testResult?.degradation_curve || [];
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Stress Test Executive Report - ${projectName}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background: #f8fafc; color: #0f172a; padding: 40px; margin: 0; }
    .card { background: #ffffff; border-radius: 16px; border: 1px solid #e2e8f0; padding: 24px; margin-bottom: 24px; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
    .title { font-size: 24px; font-weight: 800; color: #0f172a; margin-bottom: 4px; }
    .subtitle { font-size: 13px; color: #64748b; margin-bottom: 20px; }
    .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }
    .kpi { background: #f8fafc; border-radius: 12px; padding: 20px; text-align: center; border: 1px solid #e2e8f0; }
    .kpi-val { font-size: 32px; font-weight: 900; color: #dc2626; margin-top: 8px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th { background: #f1f5f9; text-align: left; padding: 12px; font-size: 12px; color: #64748b; border-bottom: 2px solid #e2e8f0; }
    td { padding: 12px; font-size: 13px; border-bottom: 1px solid #f1f5f9; }
    .badge { padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 800; display: inline-block; }
    .healthy { background: #ecfdf5; color: #16a34a; }
    .degraded { background: #fef3c7; color: #d97706; }
    .breaking { background: #ffedd5; color: #c2410c; }
    .collapsed { background: #fee2e2; color: #dc2626; }
  </style>
</head>
<body>
  <div class="card">
    <div class="title">Stress Test Executive Audit Report</div>
    <div class="subtitle">Project: <strong>${projectName}</strong> | Target: <code>${targetUrl}</code> | Execution ID: <code>${runId || 'STRESS-RUN'}</code></div>
    
    <div class="grid">
      <div class="kpi">
        <div style="font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase;">System Breaking Point</div>
        <div class="kpi-val">${testResult?.breaking_point_vus ? testResult.breaking_point_vus + ' VUs' : 'Not Breached'}</div>
        <div style="font-size: 11px; color: #dc2626; margin-top: 4px;">Breached ${errorThreshold}% Error Threshold</div>
      </div>
      <div class="kpi">
        <div style="font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase;">Max Sustainable Throughput</div>
        <div class="kpi-val" style="color: #FD6101;">${testResult?.max_sustainable_rps || 0} RPS</div>
        <div style="font-size: 11px; color: #16a34a; margin-top: 4px;">Measured Optimal Capacity</div>
      </div>
      <div class="kpi">
        <div style="font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase;">Collapse Root Cause</div>
        <div style="font-size: 13px; font-weight: 800; color: #0f172a; margin-top: 8px;">${testResult?.failure_reason || 'Target load under execution'}</div>
        <div style="font-size: 11px; color: #64748b; margin-top: 4px;">${testResult?.confidence_score || 'POSSIBLE (55% Confidence)'}</div>
      </div>
    </div>

    <h3>System Degradation & Latency Percentiles Breakdown</h3>
    <table>
      <thead>
        <tr>
          <th>Load Stage (VUs)</th>
          <th>p50 Latency</th>
          <th>p90 Latency</th>
          <th>p95 Latency</th>
          <th>p99 Latency</th>
          <th>Error Rate %</th>
          <th>Throughput (RPS)</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${curve.map(row => `
          <tr>
            <td><strong>${row.vus} VUs</strong></td>
            <td>${row.p50_latency || row.p95_latency}</td>
            <td>${row.p90_latency || row.p95_latency}</td>
            <td><strong>${row.p95_latency}</strong></td>
            <td>${row.p99_latency || row.p95_latency}</td>
            <td><strong>${row.error_rate}</strong></td>
            <td>${row.rps || 0} RPS</td>
            <td><span class="badge ${row.status === 'HEALTHY' ? 'healthy' : row.status === 'DEGRADED' ? 'degraded' : row.status === 'BREAKING_POINT' ? 'breaking' : 'collapsed'}">${row.status}</span></td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>
</body>
</html>
    `;
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Stress_Test_Report_${projectName.replace(/\s+/g, '_')}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "28px 32px 48px", color: "#0f172a", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header Card */}
      <div style={{
        background: "#ffffff",
        border: "1px solid rgba(220,38,38,0.15)", borderRadius: 20, padding: "24px 28px",
        marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            background: "#FD6101", color: "#fff",
            width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, boxShadow: "0 4px 14px rgba(253,97,1,0.3)"
          }}><Flame size={24} /></div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: "#0f172a", display: "flex", alignItems: "center", gap: 12 }}>
              Stress Testing Engine
              <span style={{ fontSize: 11, background: "#dc2626", color: "#ffffff", padding: "4px 12px", borderRadius: 20, fontWeight: 800, textTransform: "uppercase" }}>
                Auditable Breaking Point Engine
              </span>
            </h1>
            <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 0 0" }}>
              Dynamic HTTP load generator calculating p50/p90/p95/p99 latency percentiles & failure thresholds
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {loading && (
            <button onClick={handleStopTest} style={{
              background: "#ef4444", color: "#fff", border: "none", padding: "12px 20px", borderRadius: 12,
              fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 14px rgba(239,68,68,0.3)"
            }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Octagon size={14} /> Emergency STOP</span>
            </button>
          )}

          <button onClick={handleRunStressTest} disabled={loading} style={{
            background: "#FD6101", color: "#fff",
            border: "none", padding: "12px 24px", borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: "pointer",
            boxShadow: "0 4px 14px rgba(253,97,1,0.3)", opacity: loading ? 0.7 : 1
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#e05300"}
          onMouseLeave={e => e.currentTarget.style.background = "#FD6101"}>
            {loading ? "Running HTTP Stress Test..." : <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Play size={14} /> Execute Stress Test</span>}
          </button>
        </div>
      </div>

      {/* Error Notification Alert Banner */}
      {errorMsg && (
        <div style={{
          backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 14, padding: "16px 20px",
          marginBottom: 24, color: "#991b1b", fontSize: 13, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><AlertTriangle size={15} /> {errorMsg}</div>
          <button onClick={() => setErrorMsg(null)} style={{ background: "none", border: "none", color: "#991b1b", cursor: "pointer", display: "flex", alignItems: "center" }}><X size={16} /></button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 12, padding: 4, border: "1px solid #e2e8f0", marginBottom: 24, width: "fit-content" }}>
        <button onClick={() => setActiveTab("builder")} style={{ padding: "8px 18px", borderRadius: 8, fontWeight: activeTab === "builder" ? 700 : 500, fontSize: 13, border: "none", background: activeTab === "builder" ? "#ffffff" : "transparent", color: activeTab === "builder" ? "#dc2626" : "#64748b", cursor: "pointer" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Sliders size={14} /> Stress Scenario Configuration</span>
        </button>
        <button onClick={() => setActiveTab("report")} style={{ padding: "8px 18px", borderRadius: 8, fontWeight: activeTab === "report" ? 700 : 500, fontSize: 13, border: "none", background: activeTab === "report" ? "#ffffff" : "transparent", color: activeTab === "report" ? "#dc2626" : "#64748b", cursor: "pointer" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><BarChart2 size={14} /> Breaking Point Executive Report</span>
        </button>
      </div>

      {/* Tab 1: Builder */}
      {activeTab === "builder" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Left Configuration Form */}
          <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Sliders size={16} color="var(--primary)" /> Stress Load Profile Parameters</span>
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>Target Base URL</label>
              <input type="url" value={targetUrl} onChange={e => setTargetUrl(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none" }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>Project Name</label>
              <input type="text" value={projectName} onChange={e => setProjectName(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Start VUs</label>
                <input type="number" value={startVus} onChange={e => setStartVus(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Step Increment</label>
                <input type="number" value={stepIncrementVus} onChange={e => setStepIncrementVus(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 4 }}>Max Target VUs</label>
                <input type="number" value={maxVus} onChange={e => setMaxVus(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>Step Duration (s)</label>
                <input type="number" value={stepDuration} onChange={e => setStepDuration(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>Failure Error Threshold (%)</label>
                <input type="number" value={errorThreshold} onChange={e => setErrorThreshold(e.target.value)} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
              </div>
            </div>

            {/* Target Authorization Checkbox */}
            <div style={{ padding: 12, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10 }}>
              <input type="checkbox" id="authConfirm" checked={targetAuthConfirmed} onChange={e => setTargetAuthConfirmed(e.target.checked)} style={{ cursor: "pointer" }} />
              <label htmlFor="authConfirm" style={{ fontSize: 11, fontWeight: 600, color: "#475569", cursor: "pointer" }}>
                I confirm explicit authorization to execute stress load testing against target: <code style={{ color: "#dc2626" }}>{targetUrl}</code>
              </label>
            </div>
          </div>

          {/* Right Steps Scenario Table */}
          <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Activity size={16} color="var(--primary)" /> Stress User Scenario Workflows</span>
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {steps.map((st, idx) => (
                <div key={idx} style={{ padding: 14, background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{st.name}</div>
                    <div style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace", marginTop: 2 }}>{st.method} {st.endpoint}</div>
                  </div>
                  <span style={{ padding: "4px 10px", borderRadius: 20, background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: 11 }}>
                    Active Stress Step
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Executive Report */}
      {activeTab === "report" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Execution Metadata & Download Toolbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", padding: "14px 20px", borderRadius: 14, border: "1px solid #e2e8f0", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>
              EXECUTION ID: <span style={{ color: "#0f172a", fontFamily: "monospace" }}>{runId || "STRESS-RUN-DATA-DRIVEN"}</span>
            </div>

            {/* Download Buttons Toolbar */}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button onClick={handleExportJSON} style={{
                background: "#f1f5f9", color: "#0f172a", border: "1px solid #cbd5e1", padding: "6px 14px", borderRadius: 8,
                fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Download size={13} /> JSON Payload</span>
              </button>

              <button onClick={handleExportCSV} style={{
                background: "#f1f5f9", color: "#0f172a", border: "1px solid #cbd5e1", padding: "6px 14px", borderRadius: 8,
                fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6
              }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Download size={13} /> CSV Latencies</span>
              </button>

              <button onClick={handleExportHTML} style={{
                background: "#FD6101", color: "#ffffff", border: "none", padding: "6px 14px", borderRadius: 8,
                fontSize: 12, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(253,97,1,0.25)"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#e05300"}
              onMouseLeave={e => e.currentTarget.style.background = "#FD6101"}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><FileText size={14} /> Print / Download HTML/PDF Report</span>
              </button>
            </div>
          </div>

          {/* Breaking Point KPI Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
            <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0", textAlign: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
                System Breaking Point
                <span style={{ background: "#dbeafe", color: "#1e40af", padding: "2px 6px", borderRadius: 10, fontSize: 9, fontWeight: 800 }}><span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#2563eb", marginRight: 4 }} /> CALCULATED</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#dc2626", marginTop: 6 }}>
                {testResult?.breaking_point_vus ? (
                  <>{testResult.breaking_point_vus} <span style={{ fontSize: 16 }}>VUs</span></>
                ) : (
                  <span style={{ fontSize: 20, color: "#16a34a" }}>Not Breached</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4, fontWeight: 600 }}>Error rate breached {errorThreshold}% threshold</div>
            </div>

            <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0", textAlign: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
                Max Sustainable Throughput
                <span style={{ background: "#dbeafe", color: "#1e40af", padding: "2px 6px", borderRadius: 10, fontSize: 9, fontWeight: 800 }}><span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#2563eb", marginRight: 4 }} /> CALCULATED</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#FD6101", marginTop: 6 }}>
                {testResult?.max_sustainable_rps || 0} <span style={{ fontSize: 16 }}>RPS</span>
              </div>
              <div style={{ fontSize: 12, color: "#16a34a", marginTop: 4, fontWeight: 600 }}>Measured optimal operating capacity</div>
            </div>

            <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0", textAlign: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
                Inferred Collapse Root Cause
                <span style={{ background: "#ffedd5", color: "#c2410c", padding: "2px 6px", borderRadius: 10, fontSize: 9, fontWeight: 800 }}><span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#ea580c", marginRight: 4 }} /> INFERRED</span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginTop: 10 }}>
                {testResult?.failure_reason || "Target load under execution"}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, fontWeight: 600 }}>
                {testResult?.confidence_score || "POSSIBLE (55% Confidence)"}
              </div>
            </div>
          </div>

          {/* Detailed Percentiles Breakdown Table */}
          <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><BarChart2 size={16} color="#dc2626" /> System Degradation & Latency Percentiles Breakdown</span>
            </h3>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>Load Stage (VUs)</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>p50 Latency</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>p90 Latency</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>p95 Latency</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>p99 Latency</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>Error Rate %</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>Throughput (RPS)</th>
                  <th style={{ padding: 12, fontSize: 12, color: "#64748b" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {testResult?.degradation_curve?.length > 0 ? (
                  testResult.degradation_curve.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: 12, fontWeight: 700, fontSize: 13 }}>{row.vus} VUs</td>
                      <td style={{ padding: 12, fontSize: 13 }}>{row.p50_latency || row.p95_latency}</td>
                      <td style={{ padding: 12, fontSize: 13 }}>{row.p90_latency || row.p95_latency}</td>
                      <td style={{ padding: 12, fontSize: 13, fontWeight: 700, color: row.status === "BREAKING_POINT" ? "#dc2626" : "#0f172a" }}>
                        {row.p95_latency}
                      </td>
                      <td style={{ padding: 12, fontSize: 13 }}>{row.p99_latency || row.p95_latency}</td>
                      <td style={{ padding: 12, fontSize: 13, fontWeight: 700, color: parseFloat(row.error_rate) > errorThreshold ? "#dc2626" : "#16a34a" }}>
                        {row.error_rate}
                      </td>
                      <td style={{ padding: 12, fontSize: 13 }}>{row.rps || 0} RPS</td>
                      <td style={{ padding: 12 }}>
                        <span style={{
                          padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 800,
                          background: row.status === "HEALTHY" ? "#ecfdf5" : row.status === "DEGRADED" ? "#fef3c7" : row.status === "BREAKING_POINT" ? "#ffedd5" : "#fee2e2",
                          color: row.status === "HEALTHY" ? "#16a34a" : row.status === "DEGRADED" ? "#d97706" : row.status === "BREAKING_POINT" ? "#c2410c" : "#dc2626"
                        }}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ padding: 24, textAlign: "center", color: "#64748b", fontSize: 13 }}>
                      No metric samples captured yet. Click <strong>"Execute Stress Test"</strong> to run a live execution run against your target URL.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* System Recovery Analysis Card */}
          <div style={{ backgroundColor: "#ffffff", borderRadius: 18, padding: 24, border: "1px solid #e2e8f0" }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><Activity size={16} color="#059669" /> Post-Collapse System Recovery Analysis</span>
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 16, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Recovery Lag Time</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a", marginTop: 4 }}>
                  {testResult?.recovery_analysis?.recovery_lag_ms || "0ms"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Recovery Status</div>
                <span style={{
                  display: "inline-block", marginTop: 6, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 800,
                  background: testResult?.recovery_analysis?.recovery_status === "SUCCESS" ? "#ecfdf5" : "#fee2e2",
                  color: testResult?.recovery_analysis?.recovery_status === "SUCCESS" ? "#16a34a" : "#dc2626"
                }}>
                  {testResult?.recovery_analysis?.recovery_status || "NOMINAL"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
                {testResult?.recovery_analysis?.recovery_details || "Evaluates whether system latency normalizes back below SLA thresholds after load reduction."}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

